import { useCallback, useMemo, useState, useEffect } from 'react';
import { useActionLock } from '../../../../../hooks/useActionLock';
import { normalizeFixed } from '../../../../../helpers/helpers';
import { clearSelectedComponent } from '../../../../../store/reducers/actions';
const { v4: uuidv4 } = require('uuid');
/**
 * Módulo headless de Frete do Movimento
 *
 * Responsável por:
 * - state.draft (novoFrete)
 * - state.itensFrete + rowCountFrete
 * - actions.change/selectRow/save/clear/setRemote
 *
 * Observação:
 * - fetch remoto (listar) fica no Form.js (fetchData),
 *   mas o módulo expõe setRemote para padronizar.
 */
export function useFreteModule(params) {
    const {
        dados,
        setDados,
        natureza,
        //apis
        CRUDsAPI,
        DataListAPI,
        MovimentoAPI,
        PessoaAPI,

        setFlashMessage,
        formatarNumeroDecimal,
        operacaoParametros,
        mrt,
    } = params;

    // =========================
    // GRID
    // =========================
    const [itensFrete, setItensFrete] = useState([]);
    const [rowCountFrete, setRowCountFrete] = useState(0);

    // =========================
    // STATES AUX (datalist)
    // =========================
    const [Transportadora, setTransportadora] = useState({});
    const [OptionsTransportador, setOptionsTransportador] = useState([]);

    // =========================
    // DRAFT
    // =========================
    const [draft, setDraft] = useState({
        id: null,
        uuid: null,

        movimentoId: dados?.id ?? null,
        empresaId: dados?.empresaId ?? null,

        transportadoraId: null,
        Transportadora: {},

        tipoFrete: '',
        responsavelFinanceiro: '',

        formaId: null,
        prazoId: null,
        contaId: null,
        centroCustoId: null,

        provisao: false,

        valorFrete: '0.00',
        valorSeguro: '0.00',
        valorPedagio: '0.00',
        valorAlimentacao: '0.00',
        valorOutros: '0.00',
        valorTotal: '0.00',

        obs: '',

        ativo: true,
        natureza,

        funcao: 'Adicionar',
        index: null,
    });

    // =========================
    // HELPERS
    // =========================
    const commitRemote = useCallback((list, totalRowCount) => {
        const safe = Array.isArray(list) ? list : [];
        setItensFrete(safe);

        const total = Number(totalRowCount);

        setRowCountFrete(Number.isFinite(total) ? total : safe.length);
    }, []);
    const calcTotal = useCallback((p) => {
        const v = (x) => Number(x || 0);
        const total = v(p.valorFrete) + v(p.valorSeguro) + v(p.valorPedagio) + v(p.valorAlimentacao) + v(p.valorOutros);

        return total.toFixed(2);
    }, []);
    const getKeyValueFrete = useCallback((obj) => {
        if (!obj) return null;
        return obj.uuid || obj.id || null;
    }, []);
    const isEmpty = (v) => v === null || v === undefined || String(v).trim() === '';

    const applyDefaultsToDraft = useCallback(
        (prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                tipoFrete: isEmpty(prev.tipoFrete) ? (operacaoParametros?.freteTipoPadrao ?? '') : prev.tipoFrete,
                responsavelFinanceiro: isEmpty(prev.responsavelFinanceiro)
                    ? (operacaoParametros?.freteResponsavelFinanceiro ?? '')
                    : prev.responsavelFinanceiro,

                formaId: isEmpty(prev.formaId) ? (operacaoParametros?.freteFormaId ?? null) : prev.formaId,
                prazoId: isEmpty(prev.prazoId) ? (operacaoParametros?.fretePrazoId ?? null) : prev.prazoId,
                contaId: isEmpty(prev.contaId) ? (operacaoParametros?.freteContaId ?? null) : prev.contaId,
                centroCustoId: isEmpty(prev.centroCustoId)
                    ? (operacaoParametros?.freteCentroCustoId ?? null)
                    : prev.centroCustoId,

                geraFinanceiro:
                    prev.geraFinanceiro === null || prev.geraFinanceiro === undefined
                        ? (operacaoParametros?.freteGeraFinanceiro ?? false)
                        : prev.geraFinanceiro,

                provisao:
                    prev.provisao === null || prev.provisao === undefined
                        ? (operacaoParametros?.freteProvisao ?? false)
                        : prev.provisao,
            };
        },
        [operacaoParametros]
    );

    const normalizeMoneyValue = (value, decimals = 2) => {
        if (value == null || value === '') return Number(0).toFixed(decimals);

        if (typeof value === 'number') {
            return Number(value || 0).toFixed(decimals);
        }

        const str = String(value).trim();

        if (!str) return Number(0).toFixed(decimals);

        // formato BR: 1.234,56
        if (str.includes(',')) {
            const parsed = Number(str.replace(/\./g, '').replace(',', '.'));
            return Number.isFinite(parsed) ? parsed.toFixed(decimals) : Number(0).toFixed(decimals);
        }

        // formato simples: 170 ou 170.50
        const parsed = Number(str);
        return Number.isFinite(parsed) ? parsed.toFixed(decimals) : Number(0).toFixed(decimals);
    };

    const normalizeDraftToFrete = useCallback(
        (d) => {
            const empresaId = d.empresaId || dados?.empresaId || null;
            const movimentoId = d.movimentoId || dados?.id || null;

            return {
                ...d,

                empresaId,
                movimentoId,

                // ✅ só aplica default se o draft vier vazio
                tipoFrete: isEmpty(d?.tipoFrete) ? (operacaoParametros?.freteTipoPadrao ?? '') : d.tipoFrete,
                responsavelFinanceiro: isEmpty(d?.responsavelFinanceiro)
                    ? (operacaoParametros?.freteResponsavelFinanceiro ?? '')
                    : d.responsavelFinanceiro,

                formaId: isEmpty(d?.formaId) ? (operacaoParametros?.freteFormaId ?? null) : d.formaId,
                prazoId: isEmpty(d?.prazoId) ? (operacaoParametros?.fretePrazoId ?? null) : d.prazoId,
                contaId: isEmpty(d?.contaId) ? (operacaoParametros?.freteContaId ?? null) : d.contaId,
                centroCustoId: isEmpty(d?.centroCustoId) ? (operacaoParametros?.freteCentroCustoId ?? null) : d.centroCustoId,

                geraFinanceiro:
                    d?.geraFinanceiro == null ? (operacaoParametros?.freteGeraFinanceiro ?? false) : Boolean(d.geraFinanceiro),

                provisao: d?.provisao == null ? (operacaoParametros?.freteProvisao ?? false) : Boolean(d.provisao),

                // normalizações numéricas
                valorFrete: normalizeMoneyValue(d.valorFrete, 2),
                valorSeguro: normalizeMoneyValue(d.valorSeguro, 2),
                valorPedagio: normalizeMoneyValue(d.valorPedagio, 2),
                valorOutros: normalizeMoneyValue(d.valorOutros, 2),
                valorAlimentacao: normalizeMoneyValue(d.valorAlimentacao, 2),
                valorTotal: normalizeMoneyValue(d.valorTotal, 2),

                // entidades
                Transportadora: d.Transportadora || {},
                Empresa: d.Empresa || {},
            };
        },
        [dados?.empresaId, dados?.id, operacaoParametros]
    );

    const mergeDadosMovimentoFrete = useCallback(
        (lista) => {
            setDados((prev) => ({
                ...prev,
                Fretes: Array.isArray(lista) ? lista : [], // ✅
            }));
        },
        [setDados]
    );

    const commitList = useCallback(
        (lista) => {
            const safe = Array.isArray(lista) ? lista : [];
            setItensFrete(safe);
            setRowCountFrete(safe.length);
            mergeDadosMovimentoFrete(safe);
        },
        [mergeDadosMovimentoFrete]
    );
    const firstNonEmpty = (...vals) => {
        for (const v of vals) {
            if (v !== null && v !== undefined && String(v).trim() !== '') return v;
        }
        return '';
    };
    const enrichTransportadora = useCallback(
        async (obj) => {
            const transportadoraId = obj?.transportadoraId ?? null;

            // se já tiver descrição, não busca de novo
            const jaTemDescricao = obj?.Transportadora?.descricao && String(obj.Transportadora.descricao).trim() !== '';

            if (!transportadoraId || jaTemDescricao) {
                return {
                    ...obj,
                    Transportadora: {
                        id: transportadoraId ?? obj?.Transportadora?.id ?? null,
                        descricao: obj?.Transportadora?.descricao ?? '',
                    },
                };
            }

            const response = await PessoaAPI.pessoaById(transportadoraId);
            const transportadora = response?.data ?? null;

            return {
                ...obj,
                Transportadora: {
                    id: transportadoraId ?? transportadora?.id ?? null,
                    descricao: transportadora?.descricao ?? '',
                },
            };
        },
        [PessoaAPI]
    );

    const clear = useCallback(
        ({ keepDefaults = false } = {}) => {
            setDraft((prev) => {
                // base limpa
                const base = {
                    funcao: 'Adicionar',
                    uuid: null,
                    id: null,
                    movimentoId: prev?.movimentoId ?? null,
                    empresaId: prev?.empresaId ?? null,

                    transportadoraId: null,
                    Transportadora: {},

                    obs: '',

                    valorFrete: '0.00',
                    valorSeguro: '0.00',
                    valorPedagio: '0.00',
                    valorOutros: '0.00',
                    valorAlimentacao: '0.00',
                    valorTotal: '0.00',
                    valorEntrada: '0.00',

                    // campos “parametrizáveis”
                    tipoFrete: '',
                    responsavelFinanceiro: '',
                    formaId: null,
                    prazoId: null,
                    contaId: null,
                    centroCustoId: null,
                    geraFinanceiro: null,
                    provisao: null,
                };
                setTransportadora({});

                // ✅ se manter defaults, aplica normalize (que já sabe puxar operacaoParametros)
                if (keepDefaults) {
                    return normalizeDraftToFrete(base);
                }

                return base;
            });
        },
        [setDraft, normalizeDraftToFrete]
    );

    // =========================
    // ACTIONS
    // =========================

    /**
     * change roteia:
     * - Transportadora.descricao (DataList)
     * - novoFrete.* (draft)
     */
    const change = useCallback(
        async (e) => {
            const { name, value, checked, type } = e.target;

            // ==== Datalist Transportadora ====
            if (name === 'Transportadora.descricao') {
                const texto = value;

                setTransportadora((prev) => ({ ...prev, descricao: texto }));

                // Você já tem endpoint padrão de datalist. Ajuste conforme seu projeto:
                const result = await DataListAPI.dataList({
                    empresaId: dados?.empresaId,
                    tabela: 'Pessoa',
                    condicao: 'transportadora', // ajuste se você usa algo diferente
                    filtro: texto,
                });

                setOptionsTransportador(result || []);

                // pega id pelo datalist (padrão do seu Form)
                const selectedId = document.querySelector(`datalist option[value="${texto}"]`)?.dataset.id || null;

                setDraft((prev) => ({
                    ...prev,
                    transportadoraId: selectedId,
                }));

                return;
            }

            // ==== Draft novoFrete ====
            if (!name.startsWith('novoFrete.')) return;
            const campo = name.split('.')[1];

            // campos moeda
            const moneyFields = ['valorFrete', 'valorSeguro', 'valorPedagio', 'valorAlimentacao', 'valorOutros'];

            if (moneyFields.includes(campo)) {
                const valorFormatado = formatarNumeroDecimal(value);

                setDraft((prev) => {
                    const next = { ...prev, [campo]: valorFormatado };
                    return { ...next, valorTotal: calcTotal(next) };
                });
                return;
            }

            // checkbox
            if (type === 'checkbox') {
                setDraft((prev) => ({ ...prev, [campo]: checked }));
                return;
            }

            // default
            setDraft((prev) => ({ ...prev, [campo]: value }));
        },
        [DataListAPI, dados?.empresaId, formatarNumeroDecimal, calcTotal]
    );

    const selectRow = useCallback(
        async (rowOriginal, rowIndex, funcao) => {
            const row = rowOriginal || {};
            const isCancelar = (funcao || '')?.toLowerCase() === 'cancelar';

            setDraft((prev) => ({
                ...prev,
                id: row?.id ?? null,
                uuid: row?.uuid ?? null,

                movimentoId: row?.movimentoId ?? dados?.id ?? null,
                empresaId: row?.empresaId ?? dados?.empresaId ?? null,

                transportadoraId: row?.transportadoraId ?? row?.Transportadora?.id ?? null,
                Transportadora: row?.Transportadora ?? {},

                tipoFrete: row?.tipoFrete ?? '',
                responsavelFinanceiro: row?.responsavelFinanceiro ?? '',

                formaId: row?.formaId ?? null,
                prazoId: row?.prazoId ?? null,
                contaId: row?.contaId ?? null,
                centroCustoId: row?.centroCustoId ?? null,

                provisao: !!row?.provisao,

                valorFrete: row?.valorFrete ?? '0.00',
                valorSeguro: row?.valorSeguro ?? '0.00',
                valorPedagio: row?.valorPedagio ?? '0.00',
                valorAlimentacao: row?.valorAlimentacao ?? '0.00',
                valorOutros: row?.valorOutros ?? '0.00',
                valorTotal: row?.valorTotal ?? '0.00',

                obs: row?.obs ?? '',

                funcao: funcao || 'Adicionar',
                index: rowIndex,

                // se cancelar, você mantém a intenção no botão do form
                // (o backend vai interpretar como cancelamento)
                ativo: isCancelar ? false : true,
            }));

            // sincroniza datalist
            if (row?.Transportadora) setTransportadora(row.Transportadora);
            else setTransportadora({});
        },
        [dados?.id, dados?.empresaId]
    );
    const upsertLocal = useCallback(
        (lista, d) => {
            const safe = Array.isArray(lista) ? lista : [];

            // ✅ garante uuid antes de calcular a chave
            const dWithUuid = { ...d, uuid: d?.uuid || uuidv4() };

            const keyDraft = getKeyValueFrete(dWithUuid);
            if (!keyDraft) return safe;

            const normalized = { ...normalizeDraftToFrete(d), uuid: d.uuid || uuidv4() };

            const idx = safe.findIndex((it) => getKeyValueFrete(it) === keyDraft);

            // inserir
            if (idx === -1) {
                return [...safe, normalized];
            }

            // editar
            const copy = [...safe];
            copy[idx] = {
                ...copy[idx],
                ...normalized,

                // preserva relacionamentos carregados
                Transportadora: normalized.Transportadora || copy[idx].Transportadora,
                Empresa: normalized.Empresa || copy[idx].Empresa,
            };

            return copy;
        },
        [normalizeDraftToFrete, getKeyValueFrete]
    );

    const save = useCallback(async () => {
        const d0 = draft;

        const movimentoId = d0?.movimentoId ?? dados?.id ?? null;
        const empresaId = d0?.empresaId ?? dados?.empresaId ?? null;

        // ✅ normalize primeiro (pra aplicar defaults e formatos)
        const d = normalizeDraftToFrete({
            ...d0,
            movimentoId,
            empresaId,
        });

        if (!d?.empresaId) {
            setFlashMessage('Empresa não identificada.', 'warning');
            return;
        }

        if (!d?.valorTotal || Number(d.valorTotal) <= 0) {
            setFlashMessage('Informe o valor total do frete.', 'warning');
            return;
        }

        // ✅ ENRIQUECE transportadora (mesmo padrão do FormEntrada)
        let dFinal;
        try {
            dFinal = await enrichTransportadora(d);
        } catch (e) {
            // não bloqueia o fluxo: só segue sem descrição (mas normalmente não vai falhar)
            dFinal = d;
        }

        // adicionar, editar, cancelar
        const funcao = (dFinal?.funcao || '')?.toLowerCase();
        // ============================
        // REMOTO (movimento já gravado)
        // ============================
        if (dados?.uuid) {
            // para criar, o backend usa movimentoUuid na rota
            const movimentoUuid = dados.uuid;

            try {
                const result = await MovimentoAPI.managerFrete(dFinal);

                setFlashMessage(result?.message || 'Frete processado', result?.type || 'success');

                const funcao = (dFinal?.funcao || '')?.toLowerCase();

                if (funcao === 'cancelar') {
                    const updated = (itensFrete || []).filter((item) => {
                        const keyItem = item?.uuid || item?.id;
                        const keyResult = result?.data?.uuid || dFinal?.uuid || dFinal?.id;
                        return keyItem !== keyResult;
                    });

                    commitList(updated);
                    clear({ keepDefaults: true });
                    return;
                }

                const saved = result?.data;
                const updated = upsertLocal(itensFrete, saved);
                commitList(updated);

                clear({ keepDefaults: true });
                return;
            } catch (error) {
                console.error(error);
                if (error?.response?.status === 422) {
                    setFlashMessage('Erro de validação: ' + (error.response.data?.message || ''), 'error');
                } else {
                    setFlashMessage('Erro ao processar frete.', 'error');
                }
                return;
            }
        }

        // ============================
        // LOCAL (movimento ainda não gravado)
        // ============================
        const updated = upsertLocal(itensFrete, {
            ...dFinal,
            movimentoId: null,
            pendente: true,
        });

        commitList(updated);
        clear();
    }, [
        draft,
        dados?.uuid,
        dados?.id,
        dados?.empresaId,
        itensFrete,
        MovimentoAPI,
        setFlashMessage,
        upsertLocal,
        commitList,
        clear,
        normalizeDraftToFrete,
        enrichTransportadora,
    ]);

    const canSave = useMemo(() => {
        return Number(draft?.valorTotal || 0) > 0;
    }, [draft?.valorTotal]);

    useEffect(() => {
        if (!operacaoParametros) return;

        setDraft((prev) => applyDefaultsToDraft(prev));
    }, [operacaoParametros, applyDefaultsToDraft, setDraft]);

    const main = {
        state: dados,
    };

    const draftContract = {
        value: draft,
        setValue: setDraft,
        clear,
        selectRow,
    };

    const list = {
        items: itensFrete,
        rowCount: rowCountFrete,
        setItems: setItensFrete,
        setRowCount: setRowCountFrete,
    };

    const mrtContract = {
        raw: mrt,
        state: {
            columnFilterFns: mrt?.columnFilterFns ?? {},
            columnFilters: mrt?.columnFilters ?? [],
            globalFilter: mrt?.globalFilter ?? '',
            pagination: mrt?.pagination ?? { pageIndex: 0, pageSize: 5 },
            sorting: mrt?.sorting ?? [],
        },
        setters: {
            setColumnFilterFnsMode: mrt?.setColumnFilterFnsMode || mrt?.setColumnFiltersMode,
            setColumnFilters: mrt?.setColumnFilters,
            setGlobalFilter: mrt?.setGlobalFilter,
            setPagination: mrt?.setPagination,
            setSorting: mrt?.setSorting,
        },
        flags: {
            isLoading: false,
            isRefetching: false,
            isError: false,
        },
    };

    // flags
    const saveLock = useActionLock();
    //    const refreshLock = useActionLock();
    const flags = { canSave, isSaving: saveLock.isLocked /*isRefreshing: refreshLock.isLocked */ };

    const actions = {
        change,
        focus: async () => {},
        blur: async () => {},
        clear,
        save: async () =>
            await saveLock.run(async () => {
                return await save();
            }),
        submitDraft: async () =>
            await saveLock.run(async () => {
                return save();
            }),
        selectRow,
        setRemote: commitRemote,
        setTransportadora,
        setOptionsTransportador,
    };

    const state = {
        draft,
        setDraft,
        itensFrete,
        rowCountFrete,
        Transportadora,
        OptionsTransportador,
        canSave,
    };

    return {
        key: 'frete',

        main,
        draft: draftContract,
        list,
        mrt: mrtContract,
        flags,
        actions,

        services: {
            commitRemote,
            calcTotal,
            getKeyValueFrete,
            applyDefaultsToDraft,
            normalizeDraftToFrete,
            mergeDadosMovimentoFrete,
            commitList,
            enrichTransportadora,
            upsertLocal,
        },

        state, // compat temporária
    };
}
